import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  booleanAttribute,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild,
  Signal, HostBinding, WritableSignal, InputSignal, InputSignalWithTransform, Optional
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup, FormGroupDirective,
  FormsModule,
  NgForm,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { FocusMonitor } from '@angular/cdk/a11y';
import {
  MAT_FORM_FIELD, MatFormField,
  MatFormFieldControl
} from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { NewValueAccessor } from '../value-accessor/new-value-accessor';

/** Data structure for holding telephone number. */
export class MaterialTelephone {
  constructor(
    public area: string,
    public exchange: string,
    public subscriber: string
  ) {}
}

/** Custom `MatFormFieldControl` for telephone number input. */
@Component({
  selector: 'td-wb-material-phone-input',
  templateUrl: 'material-phone-input.component.html',
  styleUrl: 'material-phone-input.component.scss',
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: MaterialPhoneInputComponent
    }
  ],
  imports: [FormsModule, ReactiveFormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MaterialPhoneInputComponent extends NewValueAccessor<MaterialTelephone> implements MatFormFieldControl<MaterialTelephone>, OnDestroy {
  static nextId: number = 0;
  readonly areaInput: Signal<HTMLInputElement> = viewChild.required<HTMLInputElement>('area');
  readonly exchangeInput: Signal<HTMLInputElement> = viewChild.required<HTMLInputElement>('exchange');
  readonly subscriberInput: Signal<HTMLInputElement> = viewChild.required<HTMLInputElement>('subscriber');
  readonly parts: FormGroup<{
    area: FormControl<string | null>;
    exchange: FormControl<string | null>;
    subscriber: FormControl<string | null>;
  }>;

  readonly stateChanges: Subject<void> = new Subject<void>();
  readonly touched: WritableSignal<boolean> = signal(false);
  readonly controlType: string = 'material-phone-input';
  @HostBinding('attr.id') readonly id: string = `material-phone-input-${MaterialPhoneInputComponent.nextId++}`;
  readonly _userAriaDescribedBy: InputSignal<string> = input<string>('', { alias: 'aria-describedby' });
  readonly _placeholder: InputSignal<string> = input<string>('', { alias: 'placeholder' });
  readonly _required: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(false, {
    alias: 'required',
    transform: booleanAttribute
  });

  readonly _disabledByInput: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(false, {
    alias: 'disabled',
    transform: booleanAttribute
  });

  protected readonly _formField: MatFormField = inject(MAT_FORM_FIELD, {
    optional: true
  });

  private readonly _focused: WritableSignal<boolean> = signal(false);
  private readonly _disabled: Signal<boolean> = computed(() => this._disabledByInput() || this._disabledByCva());
  private readonly _focusMonitor: FocusMonitor = inject(FocusMonitor);
  private readonly _elementRef: ElementRef<HTMLElement> = inject<ElementRef<HTMLElement>>(ElementRef);

  @HostBinding('class.td-wb-material-phone-input__floating-label')
  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  get focused(): boolean {
    return this._focused();
  }

  get empty(): boolean {
    const {
      value: { area, exchange, subscriber }
    }: { value: { area?: string | null; exchange?: string | null; subscriber?: string | null } } = this.parts;

    return !area && !exchange && !subscriber;
  }

  get userAriaDescribedBy(): string {
    return this._userAriaDescribedBy();
  }

  get placeholder(): string {
    return this._placeholder();
  }

  get required(): boolean {
    return this._required();
  }

  get disabled(): boolean {
    return this._disabled();
  }

  get errorState(): boolean {
    const parent = this.parentForm || this.parentFormGroup;
    return this.parts.invalid && (this.touched() || parent?.submitted);
  }

  constructor(@Optional() private parentForm: NgForm,
              @Optional() private parentFormGroup: FormGroupDirective) {
    super();

    this.parts = inject(FormBuilder).group({
      area: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(3)]],
      exchange: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(3)]],
      subscriber: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(4)]]
    });

    effect(() => {
      // Read signals to trigger effect.
      this._placeholder();
      this._required();
      this._disabled();
      this._focused();
      // Propagate state changes.
      untracked(() => this.stateChanges.next());
    });

    effect(() => {
      if (this._disabled()) {
        untracked(() => this.parts.disable());
      } else {
        untracked(() => this.parts.enable());
      }
    });

    /* This was included in the original but doesn't seem needed, keeping it for now until we know what behaviour we want
    This code resets the entire phone number if the user presses backspace after a complete number has been entered
    effect((): void => {
      const value = this._value() || new MaterialTelephone('', '', '');
      untracked(() => this.parts.setValue(value));
    });*/

    this.parts.statusChanges.pipe(takeUntilDestroyed()).subscribe((): void => {
      this.stateChanges.next();
    });

    this.parts.valueChanges.pipe(takeUntilDestroyed()).subscribe((): void => {
      const tel = this.parts.valid
        ? new MaterialTelephone(
          this.parts.value.area || '',
          this.parts.value.exchange || '',
          this.parts.value.subscriber || ''
        )
        : null;
      this._updateValue(tel);
    });
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
    this._focusMonitor.stopMonitoring(this._elementRef);
  }

  onFocusIn(): void {
    if (!this._focused()) {
      this._focused.set(true);
    }
  }

  onFocusOut(event: FocusEvent): void {
    if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      this.touched.set(true);
      this._focused.set(false);
      this.onTouchedCallback();
    }
  }

  autoFocusNext(control: AbstractControl, nextElement?: HTMLInputElement): void {
    if (!control.errors && nextElement) {
      this._focusMonitor.focusVia(nextElement, 'program');
    }
  }

  autoFocusPrev(control: AbstractControl, prevElement: HTMLInputElement): void {
    if (control.value.length < 1) {
      this._focusMonitor.focusVia(prevElement, 'program');
    }
  }

  setDescribedByIds(ids: Array<string>): void {
    const controlElement = this._elementRef.nativeElement.querySelector(
      '.td-wb-material-phone-input__container'
    );
    controlElement?.setAttribute('aria-describedby', ids.join(' '));
  }

  onContainerClick(): void {
    if (this.parts.controls.subscriber.valid) {
      this._focusMonitor.focusVia(this.subscriberInput(), 'program');
    } else if (this.parts.controls.exchange.valid) {
      this._focusMonitor.focusVia(this.subscriberInput(), 'program');
    } else if (this.parts.controls.area.valid) {
      this._focusMonitor.focusVia(this.exchangeInput(), 'program');
    } else {
      this._focusMonitor.focusVia(this.areaInput(), 'program');
    }
  }

  writeValue(tel: MaterialTelephone | null): void {
    this._updateValue(tel);
  }

  _handleInput(control: AbstractControl, nextElement?: HTMLInputElement): void {
    this.autoFocusNext(control, nextElement);
    this.onChangeCallback(this.value);
  }

  private _updateValue(tel: MaterialTelephone | null): void {
    const current = this.value;
    if (
      tel === current ||
      (tel?.area === current?.area &&
        tel?.exchange === current?.exchange &&
        tel?.subscriber === current?.subscriber)
    ) {
      return;
    }
    this.value = tel;
  }
}
